/**
 * Pushing newsletters to the server, without the editor ever waiting for it.
 *
 * Every edit is written to local storage first and synchronously, exactly as
 * before — undo, version history and the preview all keep working against a
 * value that is already there. This queue then carries the change to
 * `newsletter.newsletters` in the background.
 *
 * The editor mints the id, so a save is an upsert: sending the same newsletter
 * twice updates it, and a save that failed is simply sent again on the next
 * edit. Nothing here can lose work — the local copy is written before the
 * network is ever touched, and a failed save leaves the newsletter marked
 * dirty rather than dropping it.
 *
 * Last write wins. Two people editing one newsletter at the same moment will
 * overwrite each other, which is worth knowing before this is given to a team
 * bigger than two.
 */

import { saveIssue } from "./api";
import type { IssueDetail } from "./segment-types";

export type SyncState =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved"; at: number }
  | { status: "error"; message: string };

/** Exactly the fields the table stores, in a stable order so two payloads for
    the same newsletter compare equal. */
export function issuePayload(issue: IssueDetail) {
  return {
    id: issue.id,
    title: issue.title,
    subject: issue.subject,
    preheader: issue.preheader ?? null,
    status: issue.status,
    notes: issue.notes ?? null,
    sections: issue.sections ?? [],
    blocks: issue.blocks ?? [],
    categories: issue.categories ?? [],
    comments: issue.comments ?? [],
    versions: issue.versions ?? [],
  };
}

const DEBOUNCE_MS = 900;

export class IssueSync {
  /** The last payload the server acknowledged, per newsletter. */
  private saved = new Map<string, string>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private inFlight = new Set<string>();
  private onState: (state: SyncState) => void;
  /** Set when the API should not be written to at all — demo mode. */
  private readonly disabled: boolean;

  constructor(onState: (state: SyncState) => void, disabled = false) {
    this.onState = onState;
    this.disabled = disabled;
  }

  /**
   * Note the current state of every newsletter, scheduling a save for any that
   * has changed since the server last acknowledged it.
   */
  schedule(issues: IssueDetail[]): void {
    if (this.disabled) return;
    for (const issue of issues) {
      const body = JSON.stringify(issuePayload(issue));
      if (this.saved.get(issue.id) === body) continue;
      const existing = this.timers.get(issue.id);
      if (existing) clearTimeout(existing);
      this.timers.set(issue.id, setTimeout(() => {
        this.timers.delete(issue.id);
        void this.flush(issue);
      }, DEBOUNCE_MS));
    }
  }

  /** Everything the server has not acknowledged yet. */
  pending(): number {
    return this.timers.size + this.inFlight.size;
  }

  private async flush(issue: IssueDetail): Promise<void> {
    if (this.inFlight.has(issue.id)) return;
    const payload = issuePayload(issue);
    const body = JSON.stringify(payload);
    this.inFlight.add(issue.id);
    this.onState({ status: "saving" });
    try {
      await saveIssue(payload);
      this.saved.set(issue.id, body);
      this.onState({ status: "saved", at: Date.now() });
    } catch (err) {
      // Deliberately not recorded as saved, so the next edit tries again.
      this.onState({
        status: "error",
        message: err instanceof Error ? err.message : "Could not reach the server.",
      });
    } finally {
      this.inFlight.delete(issue.id);
    }
  }

  /** Drop every pending timer. The local copy is already written. */
  dispose(): void {
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
  }
}

/**
 * Put what the server has together with what this browser has.
 *
 * Seeding used to be skipped entirely whenever local storage held anything, so
 * a browser that had ever made a draft never saw a colleague's newsletters at
 * all — the one thing storing them on the server is for.
 *
 * A newsletter this browser has never synced is kept: it exists nowhere else.
 * Where both sides have the same newsletter, the one edited most recently
 * wins, which is the same rule the writes follow.
 */
export function mergeIssues(local: IssueDetail[], server: IssueDetail[]): IssueDetail[] {
  const byId = new Map<string, IssueDetail>();
  for (const issue of server) byId.set(issue.id, issue);
  for (const mine of local) {
    const theirs = byId.get(mine.id);
    if (!theirs) { byId.set(mine.id, mine); continue; }
    const mineAt = Date.parse(mine.updated_at ?? "") || 0;
    const theirsAt = Date.parse(theirs.updated_at ?? "") || 0;
    byId.set(mine.id, mineAt > theirsAt ? mine : theirs);
  }
  return [...byId.values()].sort((a, b) =>
    (Date.parse(b.updated_at ?? "") || 0) - (Date.parse(a.updated_at ?? "") || 0));
}
